<template>
  <section class="help-page">
    <Navbar />
    <main class="help-content">
      <header class="help-heading">
        <p class="eyebrow">Anleitung · Schulplanung</p>
        <h1>Hilfe zur Anwendung</h1>
        <p>Vom Erfassen einer Abwesenheit bis zum veröffentlichten Vertretungsplan.</p>
        <p class="help-meta">Stand: Oktober 2026 · Screenshots mit fiktiven Beispieldaten</p>
        <button class="button" type="button" @click="printHelp">
          Hilfe drucken / als PDF speichern
        </button>
      </header>
      <aside class="help-start" aria-labelledby="help-start-title">
        <h2 id="help-start-title">Der tägliche Ablauf</h2>
        <ol>
          <li>Unter „Lehrkräfte“ Abwesenheiten erfassen und prüfen.</li>
          <li>Den Vertretungsplan aktualisieren und das gewünschte Datum auswählen.</li>
          <li>Offene Vertretungen zuweisen, Bemerkungen prüfen und Änderungen speichern.</li>
          <li>Den Tagesplan mit „Veröffentlicht“ freigeben; bei Bedarf zusätzlich versenden.</li>
        </ol>
      </aside>
      <div class="help-search">
        <label for="help-search">Hilfe durchsuchen</label>
        <input id="help-search" v-model="query" type="search" class="input"
          placeholder="Zum Beispiel: Abwesenheit, Speichern, Epochen …">
        <p role="status">{{ filteredSections.length }} von {{ sections.length }} Themen</p>
      </div>
      <nav class="help-index" aria-label="Inhaltsverzeichnis der Hilfe">
        <a v-for="section in filteredSections" :key="section.id" :href="`#${section.id}`">
          {{ section.title }}
        </a>
      </nav>
      <p v-if="!filteredSections.length" class="help-start">
        Keine passenden Themen. Versuche einen anderen Suchbegriff oder leere das Suchfeld.
      </p>
      <article v-for="section in filteredSections" :key="section.id" :id="section.id"
        class="help-topic">
        <h2>{{ section.title }}</h2>
        <p>{{ section.intro }}</p>
        <ol v-if="section.steps.length">
          <li v-for="step in section.steps" :key="step">{{ step }}</li>
        </ol>
        <p v-for="note in section.notes" :key="note" class="help-note">{{ note }}</p>
        <figure v-if="section.image">
          <a :href="imageUrl(section.image)" target="_blank" rel="noopener"
            :aria-label="`${section.title}: Screenshot in voller Größe öffnen`">
            <img :src="imageUrl(section.image)" :alt="section.alt" loading="eager"
              width="1440" height="1000">
          </a>
          <figcaption>{{ section.caption }} · Zum Vergrößern auf das Bild klicken.</figcaption>
        </figure>
        <a href="#help-search" class="help-back">Zurück zur Themensuche ↑</a>
      </article>
    </main>
    <Footer />
  </section>
</template>

<script>
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';
import sections from '../content/help';

export default {
  components: { Navbar, Footer },
  data() { return { query: '', sections }; },
  computed: {
    filteredSections() {
      const words = this.query.trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
      return this.sections.filter((section) => {
        const text = [section.title, section.intro, ...section.steps, ...section.notes]
          .join(' ').toLocaleLowerCase('de');
        return words.every((word) => text.includes(word));
      });
    },
  },
  methods: {
    imageUrl(name) { return `${process.env.BASE_URL}help/screenshots/${name}.jpg`; },
    async printHelp() {
      this.query = '';
      await this.$nextTick();
      const images = Array.from(this.$el.querySelectorAll('figure img'));
      await Promise.all(images.map((img) => (img.decode ? img.decode().catch(() => {})
        : Promise.resolve())));
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
.help-content { max-width: 1120px; margin: 0 auto; padding: 40px 32px; }
.help-heading h1 { font-size: 2rem; font-weight: 700; margin: 8px 0 12px; }
.help-heading p { margin-bottom: 12px; }
.help-meta, figcaption { font-size: .85rem; }
h2 { font-size: 1.35rem; font-weight: 600; margin-bottom: 14px; }
.help-start { background: var(--theme-wash); border: 1px solid var(--theme-border);
  border-radius: 12px; padding: 24px; margin: 28px 0; }
ol { padding-left: 24px; margin: 18px 0; }
li { padding-left: 4px; margin-bottom: 10px; line-height: 1.65; }
.help-search label { display: block; font-weight: 600; margin-bottom: 8px; }
.help-search p { font-size: .85rem; margin-top: 8px; }
.help-index { display: flex; flex-wrap: wrap; gap: 10px; margin: 20px 0 36px; }
.help-index a { border: 1px solid var(--theme-border); border-radius: 6px; padding: 10px 14px; }
a { color: var(--theme-text); text-decoration: underline; text-underline-offset: 3px; }
a:focus-visible { outline: 3px solid var(--theme-accent); outline-offset: 4px; }
.help-topic { border-top: 1px solid var(--theme-border); padding-top: 28px;
  margin-bottom: 40px; scroll-margin-top: 80px; }
.help-topic > p { line-height: 1.7; margin-bottom: 16px; }
.help-note { border-left: 3px solid var(--theme-accent); padding: 8px 16px;
  background: var(--theme-wash); }
figure { margin: 24px 0 16px; }
figure img { display: block; width: 100%; height: auto; border: 1px solid var(--theme-border);
  border-radius: 8px; }
figcaption { margin-top: 10px; line-height: 1.5; }
.help-back { font-size: .85rem; }
@media (max-width: 600px) {
  .help-content { padding: 24px 16px; }
  .help-heading h1 { font-size: 1.65rem; }
  .help-start { padding: 18px; }
}
@media print {
  .help-search, .help-index, .help-back, .help-heading button { display: none; }
  .help-content { max-width: none; padding: 0; color: #111; }
  .help-topic { break-before: page; }
  figure { break-inside: avoid; }
  figure img { max-height: 140mm; object-fit: contain; }
}
</style>
<style>
@media print {
  .help-page header:has(.workspace-sidebar), .help-page > .foota { display: none !important; }
  .admin-workspace:has(.help-page) { padding: 0 !important; margin: 0 !important; }
  .help-page { background: white !important; color: #111 !important; }
}
</style>
